import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useTopic } from "./use-topics";
import {
  apiRequest,
  ExportMetadata,
  useAccount,
  useDatadog,
  useModus
} from "@vojomo-core/shared";
import { Comment, useCollaborative } from "./use-collaborative";
import Image from "@tiptap/extension-image";
import { useEditor } from "@tiptap/react";
import Placeholder from "@tiptap/extension-placeholder";
import StarterKit from "@tiptap/starter-kit";
import { Markdown } from "@tiptap/markdown";
import { Table, TableRow } from "@tiptap/extension-table";
import { ReadOnlyFirstColumnCell } from "components/TipTapEditorPlugins/ReadOnlyFirstColumnCell";
import Collaboration from "@tiptap/extension-collaboration";
import CollaborationCaret from "@tiptap/extension-collaboration-caret";

import { ReadOnlyTableHeader } from "components/TipTapEditorPlugins/ReadyOnlyTableHeader";
import { DisableDragDrop } from "components/TipTapEditorPlugins/DisableDragDrop";
import { TableCellSelectAll } from "components/TipTapEditorPlugins/TableCellSelectAll";
import {
  AvatarMention,
  DocumentMention,
  createMentionSuggestionOptions,
  MentionDocument
} from "components/TipTapEditorPlugins/MentionSuggestion";

import { CommentsExtension } from "../components/comments/CommentsExtension";
import { getColorForUserName } from "utils/helpers/getColorForUserName";

import {
  ySyncPluginKey,
  absolutePositionToRelativePosition,
  relativePositionToAbsolutePosition
} from "@tiptap/y-tiptap";

import * as Y from "yjs";

import {
  setEditorActiveComment,
  setEditorComments
} from "../components/comments/CommentHighlightPlugin";
import { normalizeDimensions } from "utils/helpers/sizeNormalize";
import { convertMarkdownToHTML } from "utils/helpers/stringManipulation";
import axios from "axios";

export const useOutputEditorProvider = () => {
  const { vojomoAccount, triggerGoogleOauth } = useAccount();
  const { isWaitingForResponse } = useModus();
  const {
    hasWriteAccess,
    isLatestVersion,
    isStreaming,
    currentOutputText,
    setCurrentOutputText,

    coverImage,

    topic,
    topicId,
    topicTitle,

    output
  } = useTopic();

  const {
    isSynced,
    ydoc,
    provider,

    localComments,
    activeComment,
    setActiveComment,
    updateOrphanedComments,
    updateCommentRef,
    setLocalComments,
    createComment,

    setNewComments,

    isMasterClient
  } = useCollaborative();

  const containerRef = useRef(null);

  const [showCommentButton, setShowCommentButton] = useState(false);
  const [buttonPosition, setButtonPosition] = useState({ top: 0, right: 0 });
  const [selectedRange, setSelectedRange] = useState({ from: 0, to: 0 });
  const [selectedText, setSelectedText] = useState<string>(null);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [pendingGoogleDocId, setPendingGoogleDocId] = useState<string>(null);
  const [userMentionSuggestions, setUserMentionSuggestions] = useState([]);
  const [documentMentionSuggestions, setDocumentMentionSuggestions] = useState(
    []
  );
  const { trackError } = useDatadog();

  useEffect(() => {
    const f = async () => {
      const mentionSuggestions = await apiRequest<{
        users: [];
        documents: [];
      }>({
        subRoute: "topic",
        path: `/mention_suggestions?topic_id=${topicId}`,
        method: "GET"
      });

      setUserMentionSuggestions(mentionSuggestions.users);
      setDocumentMentionSuggestions(mentionSuggestions.documents);

      console.log("Mention Suggestions", mentionSuggestions);
    };

    f();
  }, [topicId]);

  const exportToGoogleDocs = useCallback(async () => {
    setIsExporting(true);

    let googleOAuth = null;

    try {
      googleOAuth = JSON.parse(localStorage.getItem("google_oauth"));
    } catch (_) {
      triggerGoogleOauth(`exportgoogledocs_${topic["_id"]}_${output["_id"]}`);
      return;
    }

    const expiresIn = googleOAuth?.expires_in;
    const issuedAt = googleOAuth?.issued_at;
    const token = googleOAuth?.access_token;

    if (
      !token ||
      !expiresIn ||
      !issuedAt ||
      Date.now() - issuedAt > 1000 * 60 * 59
    ) {
      triggerGoogleOauth(`exportgoogledocs_${topic["_id"]}_${output["_id"]}`);
      return;
    }

    const htmlBlobArray = getExportContent();
    const file = new Blob(htmlBlobArray, {
      type: "text/html"
    });
    const metadata = {
      name: topicTitle,
      mimeType: "application/vnd.google-apps.document"
    };
    const form = new FormData();
    form.append(
      "metadata",
      new Blob([JSON.stringify(metadata)], {
        type: "application/json"
      })
    );
    form.append("file", file);

    let exportMetadatas = await apiRequest<ExportMetadata>({
      subRoute: "topic",
      path: `/get_export_metadata?ref_id=${output["_id"]}`
    });

    let googleDocumentId = exportMetadatas?.google_doc_id;

    try {
      const requestConfig = {
        headers: {
          Authorization: `Bearer ${token}`
        }
      };

      const { data } = !googleDocumentId
        ? await axios.post(
            "https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart",
            form,
            requestConfig
          )
        : await axios.put(
            `https://www.googleapis.com/upload/drive/v2/files/${googleDocumentId}?uploadType=multipart`,
            form,
            requestConfig
          );

      googleDocumentId = data.id;

      if (!exportMetadatas?.google_doc_id) {
        await apiRequest<ExportMetadata>({
          subRoute: "topic",
          path: `/update_export_metadata`,
          method: "POST",
          body: {
            refId: output["_id"],
            payload: { google_doc_id: googleDocumentId }
          }
        });
      }

      setPendingGoogleDocId(googleDocumentId);
    } catch (err) {
      trackError("error_exporting_google_docs", err, {
        topicId: topic["_id"],
        outputId: output["_id"]
      });
    } finally {
      setIsExporting(false);
    }
  }, [topicTitle, topic, output, currentOutputText, coverImage]);

  const getExportContent = () => {
    const htmlBlobArray = [];
    if (coverImage) {
      const dimensions = normalizeDimensions(1536, 1024, 640, 256);

      htmlBlobArray.push(
        `<div style="text-align:center"><img src=${coverImage.public_url} style="width:${dimensions.width}px;height:${dimensions.height}px"/></div><br/>`
      );
    }

    //Adding a newline after each paragraph
    const html = convertMarkdownToHTML(currentOutputText)
      .replaceAll("</p>", "</p><br/>")
      .replaceAll("</ul>", "</ul><br/>")
      .replaceAll("</ol>", "</ol><br/>");

    htmlBlobArray.push(html);

    htmlBlobArray.push(
      `<a href="${window.location.origin}/documents/docs/${topic["_id"]}">Crafted with Vojomo</a>`
    );

    return htmlBlobArray;
  };

  const isEditable = useMemo(() => {
    return (
      hasWriteAccess && isLatestVersion && !isStreaming && !isWaitingForResponse
    );
  }, [hasWriteAccess, isLatestVersion, isStreaming, isWaitingForResponse]);

  const onSelectionUpdate = ({ editor }) => {
    if (isSynced) {
      const { from, to } = editor.state.selection;

      const start = editor.view.coordsAtPos(from);
      const containerBounds = containerRef.current?.getBoundingClientRect();

      const top = start.top - containerBounds.top;

      const selectedText = editor.state.doc.textBetween(from, to, " ");

      setSelectedRange({ from, to });
      setSelectedText(selectedText);
      setButtonPosition({ top, right: 8 });

      setShowCommentButton(from !== to);
    }
  };

  const baseExtensions = [
    StarterKit.configure({ undoRedo: false }),
    Table,
    TableRow,
    Image,
    ReadOnlyTableHeader,
    DisableDragDrop,
    TableCellSelectAll,
    ReadOnlyFirstColumnCell.configure({
      mentionUsers: userMentionSuggestions,
      mentionDocuments: documentMentionSuggestions
    }),
    Markdown,
    AvatarMention.configure({
      HTMLAttributes: { class: "mention" },
      suggestion: createMentionSuggestionOptions(
        userMentionSuggestions,
        documentMentionSuggestions
      )
    }),
    DocumentMention.configure({
      HTMLAttributes: { class: "mention-document" }
    })
  ];

  const editorConfig = useMemo(() => {
    if (!isLatestVersion || !isSynced) {
      return {
        extensions: [...baseExtensions, CommentsExtension.configure({ ydoc })],
        editable: false,
        contentType: "markdown" as const,
        content: currentOutputText,
        onSelectionUpdate
      };
    } else {
      return {
        extensions: [
          ...baseExtensions,
          CommentsExtension.configure({ ydoc }),
          Collaboration.configure({
            fragment: ydoc.getXmlFragment("prosemirror")
          }),

          ...(provider?.awareness
            ? [
                CollaborationCaret.configure({
                  provider: provider,
                  user: {
                    name: vojomoAccount.name,
                    id: vojomoAccount["_id"],
                    color: getColorForUserName(vojomoAccount.name)
                  },
                  render: (user) => {
                    const cursor = document.createElement("span");
                    cursor.classList.add("collab-caret");
                    cursor.style.borderLeft = `2px solid ${user.color}`;
                    cursor.style.marginLeft = "-1px";
                    cursor.style.height = "1em";
                    cursor.style.position = "relative";

                    const label = document.createElement("div");
                    label.className = "collab-label";
                    label.textContent = user.name || "Anonymous";
                    label.style.position = "absolute";
                    label.style.top = "-1.2em";
                    label.style.left = "0";
                    label.style.backgroundColor = user.color;
                    label.style.color = "white";
                    label.style.padding = "0 4px";
                    label.style.fontSize = "0.65em";
                    label.style.borderRadius = "4px";
                    label.style.whiteSpace = "nowrap";

                    cursor.appendChild(label);
                    return cursor;
                  }
                })
              ]
            : []),
          Placeholder.configure({
            placeholder: "Start typing here...",
            showOnlyWhenEditable: true
          })
        ],
        editable: isEditable,
        onSelectionUpdate,
        onCreate: ({ editor }) => {
          setCurrentOutputText(
            editor.getMarkdown().replaceAll("&nbsp;", "").trim()
          );
        },
        onUpdate: ({ editor }) => {
          setCurrentOutputText(
            editor.getMarkdown().replaceAll("&nbsp;", "").trim()
          );
        }
      };
    }
  }, [isEditable, provider, isLatestVersion, isSynced]);

  const editor = useEditor(editorConfig, [editorConfig]);
  const streamingEditor = useEditor(
    {
      extensions: baseExtensions,
      contentType: "markdown",
      editable: false,
      content: currentOutputText
    },
    [currentOutputText]
  );

  const getAbsolutePosition = (relPosition: Y.RelativePosition) => {
    let absPos = -1;

    try {
      const mapping = ySyncPluginKey.getState(editor.view.state).binding
        .mapping;

      if (mapping.size > 0) {
        absPos = relativePositionToAbsolutePosition(
          ydoc,
          ydoc.getXmlFragment("prosemirror"),
          relPosition,
          mapping
        );
      }
    } catch (_) {}

    return absPos;
  };

  const getRelativePosition = (absPosition: number) => {
    let relPos = null;
    try {
      const mapping = ySyncPluginKey.getState(editor.view.state).binding
        .mapping;

      relPos = absolutePositionToRelativePosition(
        absPosition,
        ydoc.getXmlFragment("prosemirror"),
        mapping
      );
    } catch (_) {}

    return relPos;
  };

  const handleNewComment = () => {
    let fromRel = getRelativePosition(selectedRange.from);
    let toRel = getRelativePosition(selectedRange.to);

    createComment({
      parentId: null,
      text: "",
      refText: selectedText,
      fromAbs: selectedRange.from,
      toAbs: selectedRange.to,
      fromRel,
      toRel
    });

    setShowCommentButton(false);
  };

  useEffect(() => {
    const yCommentsRef = ydoc.getMap("commentsRef");

    const { from, to } = selectedRange;

    if (from === to) {
      let minDistance = Number.MAX_SAFE_INTEGER;
      let closestComment = null;

      localComments
        .filter((c) => !c.parentComment && !c.isOrphaned)
        .forEach((c) => {
          const commRef = yCommentsRef.get(c.id) as Y.Map<any>;

          const cFrom = commRef.get("from");
          const cTo = commRef.get("to");

          if (from >= cFrom && to <= cTo) {
            let currentDistanceFrom = from - cFrom;
            let currentDistanceTo = to - cTo;

            if (currentDistanceFrom < 0) {
              currentDistanceFrom = Number.MAX_SAFE_INTEGER;
            }

            if (currentDistanceTo < 0) {
              currentDistanceTo = Number.MAX_SAFE_INTEGER;
            }

            const currentDistance = Math.min(
              currentDistanceFrom,
              currentDistanceTo
            );

            if (currentDistance < minDistance) {
              minDistance = currentDistance;
              closestComment = c;
            }
          }
        });

      setActiveComment(closestComment);
    }
  }, [localComments, selectedRange]);

  useEffect(() => {
    if (editor) setEditorComments(editor, localComments);
  }, [editor, localComments]);

  useEffect(() => {
    if (editor) setEditorActiveComment(editor, activeComment);
  }, [editor, activeComment]);

  useEffect(() => {
    if (isSynced) {
      const yMap = ydoc.getMap("comments");

      const handleChange = () => {
        let areAllInitialized = true;

        for (const commentId of yMap.keys()) {
          const comm = yMap.get(commentId) as Y.Map<any>;

          if (comm.get("isInitialized") === false) {
            areAllInitialized = false;
            break;
          }
        }

        if (!areAllInitialized) {
          return;
        }

        const commentsLocal: Comment[] = [];

        for (const commentId of yMap.keys()) {
          const comm = yMap.get(commentId) as Y.Map<any>;

          if (comm.get("isDeleted")) {
            continue;
          }

          const newComm = {} as Comment;
          for (const key of comm.keys()) {
            if (key === "subComments") {
              newComm["subComments"] = [];
              const arr = comm.get("subComments").toArray();
              for (let i = 0; i < arr.length; i++) {
                newComm["subComments"].push(arr[i].toString());
              }
            } else if (key === "fromRel" || key === "toRel") {
              newComm[key] = Y.decodeRelativePosition(comm.get(key));
            } else {
              newComm[key] = comm.get(key);
            }
          }
          if (Object.keys(newComm).length > 0) {
            commentsLocal.push(newComm);
          }
        }

        const yCommentsRef = ydoc.getMap("commentsRef");

        commentsLocal.sort((a, b) => {
          const aRef = yCommentsRef.get(a.id) as Y.Map<any> | undefined;
          const bRef = yCommentsRef.get(b.id) as Y.Map<any> | undefined;

          const aFrom = aRef?.get("from") ?? null;
          const bFrom = bRef?.get("from") ?? null;

          if (aFrom === null) return 1;
          if (bFrom === null) return -1;

          return aFrom - bFrom;
        });

        console.log("Local comments");
        console.log(commentsLocal);

        setLocalComments((prev) => {
          const arr = commentsLocal.filter((c) => {
            if (c.owner.id !== vojomoAccount["_id"] && !c.text) {
              return false;
            } else {
              return true;
            }
          });

          if (prev.length > 0) {
            const filteredComments = commentsLocal.filter(
              (c) => c.owner.id !== vojomoAccount["_id"] && !!c.text
            );

            let newestComments = [];

            for (const c of filteredComments) {
              const prevComm = prev.find((pc) => pc.id === c.id);

              if (!prevComm || prevComm.text !== c.text) {
                newestComments.push(c.id);
              }
            }

            setNewComments(newestComments);
          }

          return arr;
        });
      };

      handleChange();
      yMap.observeDeep(handleChange);

      console.log("Ydoc");
      console.log(ydoc);

      return () => yMap.unobserveDeep(handleChange);
    }
  }, [ydoc, isSynced]);

  useEffect(() => {
    if (isSynced && isMasterClient) {
      const yMap = ydoc.getMap("comments");
      const yCommentsRef = ydoc.getMap("commentsRef");

      const commsToInitialize = [];

      for (const commentId of yMap.keys()) {
        const comm = yMap.get(commentId) as Y.Map<any>;
        if (!comm.get("parentComment") && comm.get("isInitialized") === false) {
          commsToInitialize.push(commentId);
        }
      }

      if (commsToInitialize.length > 0) {
        console.log("Comments to initialize", commsToInitialize);
        ydoc.transact(() => {});
        for (const commentId of commsToInitialize) {
          const commRef = yCommentsRef.get(commentId) as Y.Map<any>;
          const comm = yMap.get(commentId) as Y.Map<any>;

          const fromRel = getRelativePosition(commRef.get("from"));
          const toRel = getRelativePosition(commRef.get("to"));

          if (fromRel && toRel) {
            comm.set("fromRel", Y.encodeRelativePosition(fromRel));
            comm.set("toRel", Y.encodeRelativePosition(toRel));
          }

          comm.set("isInitialized", true);
        }
      }
    }
  }, [isSynced, isMasterClient]);

  useEffect(() => {
    if (isSynced && isMasterClient) {
      const commentsOrphanedStatus: { id: string; payload: {} }[] = [];

      for (const comment of localComments.filter((c) => !c.parentComment)) {
        let fromAbs = null;
        let toAbs = null;

        try {
          fromAbs = getAbsolutePosition(comment["fromRel"]);
          toAbs = getAbsolutePosition(comment["toRel"]);
        } catch (err) {
          console.error("Error at getting abs positions", err);
        }

        console.log("Checking comment", { text: comment.text, fromAbs, toAbs });

        if (fromAbs === -1 || toAbs === -1) {
          continue;
        }

        const isOrphaned =
          fromAbs === null || toAbs === null || fromAbs === toAbs;

        if (!isOrphaned) {
          const selectedText = editor.state.doc.textBetween(
            fromAbs,
            toAbs,
            " "
          );

          updateCommentRef(comment.id, selectedText, fromAbs, toAbs);
        }

        if (isOrphaned && !comment.isOrphaned) {
          commentsOrphanedStatus.push({
            id: comment.id,
            payload: {
              isOrphaned: true
            }
          });
        } else if (!isOrphaned && comment.isOrphaned) {
          commentsOrphanedStatus.push({
            id: comment.id,
            payload: {
              isOrphaned: false,
              fromRel: Y.encodeRelativePosition(getRelativePosition(fromAbs)),
              toRel: Y.encodeRelativePosition(getRelativePosition(toAbs))
            }
          });
        }
      }

      if (commentsOrphanedStatus.length > 0) {
        console.log("Orhpaned Update", commentsOrphanedStatus);
        updateOrphanedComments(commentsOrphanedStatus);
      }
    }
  }, [ydoc, isSynced, currentOutputText, localComments, isMasterClient]);

  return {
    editor: isStreaming ? streamingEditor : editor,

    containerRef,
    showCommentButton,
    buttonPosition,
    handleNewComment,

    getExportContent,

    isExporting,
    exportToGoogleDocs,

    pendingGoogleDocId,
    setPendingGoogleDocId
  };
};

export const OutputEditorProvider = ({
  children
}: {
  children: React.ReactNode;
  mentionDocuments?: MentionDocument[];
}) => {
  const outputEditorProvider = useOutputEditorProvider();

  return (
    <OutputEditorContext.Provider value={outputEditorProvider}>
      {children}
    </OutputEditorContext.Provider>
  );
};

export type OutputEditorProps = ReturnType<typeof useOutputEditorProvider>;
export const OutputEditorContext = createContext<OutputEditorProps>(null);
export const useOutputEditor = () => useContext(OutputEditorContext);
